import { ComponentType } from '@angular/cdk/portal';
import { VisualChartType } from '../../shared/chart/chart-type';
import { VisualComponent } from './base/Visual.component';
import { Visual } from './Visual';
import { VisualContext } from './VisualContext';
import { VisualDataSerializer } from './VisualDataSerializer';
import { VisualField } from './VisualField';
import { VisualFieldRoleTypeMap } from './VisualFieldRoleTypeMap';
import { VisualFieldSorting } from './VisualFieldSorting';
import { VisualHost } from './VisualHost';
import { VisualStyle } from './VisualStyle';

export interface VisualTypeOptions<TOptions extends Visual.Options = Visual.Options> {
  /**
   * Field roles utilized by this visual. */
  readonly fieldRoles?: VisualFieldRoleTypeMap;

  /**
   * Class name of this visual type.
   * This will be provided in some elements to allow styling based on visual type. */
  readonly className?: string;

  /**
   * Default set of visual options provided to a visual instance.
   * Note - the options are provided to the visual as inputs. */
  readonly visualOptions?: TOptions;

  /**
   * Boolean or a change-check callback, indicative of whether an instance of a visual can be reused.
   * When not provided, visuals will not be reused. */
  readonly canReuse?: boolean | ((oldOptions: TOptions, newOptions: TOptions) => boolean);

  /**
   * Factory for the inital state of the visual.
   * If null, will create an instance of Visual.State.
   * The 2nd argument may be an array of fields that the visual will receive.
   * This generally is provided in edit-mode when visual type is changed. */
  readonly createState?: (options: TOptions, fields?: ReadonlyArray<VisualField>) => Visual.State;

  /**
   * Called to capture state of a visual. */
  readonly captureState?: (state: Visual.State, context: { 
    readonly visual: Visual; 
    readonly host?: VisualHost;
  }) => boolean;

  /**
   * Factory for the inital style of the visual.
   * If null, will create an instance of VisualStyle.
   * The 2nd argument may be an array of fields that the visual will receive.
   * This generally is provided in edit-mode when visual type is changed. */
  readonly createStyle?: (options: TOptions, fields?: ReadonlyArray<VisualField>) => VisualStyle;

  /**
   * Component used for editing style of the visual.
   **/
  readonly styleEditor?: ComponentType<any>;

  /**
   * Factory creating `VisualFieldSorting` for a visual. */
  readonly fieldSorting?: (v: Visual) => VisualFieldSorting;

  /**
   * Should the data adapter sort the initial query based on the visual's field sorting?
   * Defaults to true.
   **/
  readonly includeSortingInQuery?: boolean;

  /**
   * Whether to consider the visual loaded when rendering started.
   * This allows the visual to handle loading display on its own.
   **/
  readonly loadedWhenRendering?: boolean;

  /**
   * Whether to disable sorting in layout section of visual editor.
   **/
  readonly disableLayoutSorting?: boolean;

  /**
   * Whether to show loading overlay until rendered.
   **/
  readonly showOverlayUntilRendered?: boolean;

  /**
   * Whether to clear state when visual is emptied (i.e. no fields).
   **/
  readonly clearStateOnEmpty?: boolean;

  /**
   * Does this visual show row count in UI?
   **/
  readonly showRowCount?: boolean;

  /**
   * Does this visual support background customization?
   * Defaults to `true`.
   */
  readonly hasBackground?: boolean;

  /** Fallback to original chart type, when available. */
  readonly getPbiChartType?: (v: Visual) => VisualChartType;

  readonly percentageDisplayChart?: boolean | ((v: Visual) => boolean);

  /** When true, display order of field's role is ignored when assigning display order for fields themselves. */
  readonly ignoreFieldRoleDisplayOrder?: boolean;
}

export interface IVisualType {
  get name(): string;
  get options(): VisualTypeOptions<Visual.Options>;
}

export class VisualType<
  TOptions extends Visual.Options = Visual.Options,
  TState   extends Visual.State   = Visual.State,
  TStyle   extends VisualStyle    = VisualStyle,
  TVisual  extends Visual<TOptions, TState, TStyle> = Visual<TOptions, TState, TStyle>> implements IVisualType {
  constructor(
    public readonly name: string,
    public readonly component: ComponentType<VisualComponent>,
    public readonly options: VisualTypeOptions<TOptions>
  ) { }

  public readonly fieldRoleMap = this.options.fieldRoles ?? new VisualFieldRoleTypeMap([]);

  public readonly className = this.options.className ?? this.name.replace(/[^a-z\\-_]/gi, '_');

  public readonly visualOptions = this.options.visualOptions ?? <TOptions><any>{};

  public readonly canReuse =
    (typeof this.options.canReuse === 'function')
      ? this.options.canReuse
      : () => !!this.options.canReuse;

  public createState(context: VisualContext, options: TOptions, fields?: ReadonlyArray<VisualField>): TState {
    let state = (typeof this.options.createState === 'function')
      ? this.options.createState(options, fields)
      : new Visual.State();

    state.serialize(new VisualDataSerializer(context, {}, true));

    return <TState><any>state;
  }
  
  public captureState(state: Visual.State, context: {
    readonly visual: Visual;
    readonly host?: VisualHost;
  }) {
    if (this.options.captureState) {
      return !!this.options.captureState(state, context);
    }

    return true;
  }

  public createStyle(context: VisualContext, options: TOptions, fields?: ReadonlyArray<VisualField>): TStyle {
    let style = (typeof this.options.createState === 'function')
      ? this.options.createStyle(options, fields)
      : new VisualStyle();

    style.serialize(new VisualDataSerializer(context, {}, true));

    return <TStyle><any>style;
  }

  public readonly styleEditor = this.options.styleEditor;

  public readonly fieldSorting =
    (typeof this.options.fieldSorting === 'function')
      ? this.options.fieldSorting
      : (v) => new VisualFieldSorting(v);

  public readonly includeSortingInQuery = this.options.includeSortingInQuery ?? true;

  public readonly loadedWhenRendering = this.options.loadedWhenRendering ?? false;

  public readonly disableLayoutSorting = this.options.disableLayoutSorting ?? false;

  public readonly showOverlayUntilRendered = this.options.showOverlayUntilRendered ?? false;

  public readonly clearStateOnEmpty = this.options.clearStateOnEmpty ?? true;

  public readonly showRowCount = this.options.showRowCount ?? false;

  public readonly hasBackground = this.options.hasBackground ?? true;
  
  public readonly getPbiChartType =
    (typeof this.options.getPbiChartType === 'function')
      ? this.options.getPbiChartType
      : (v) => null;
      
  public readonly percentageDisplayChart =
    (typeof this.options.percentageDisplayChart === 'function')
      ? this.options.percentageDisplayChart
      : (v) => !!this.options.percentageDisplayChart;

  public readonly ignoreFieldRoleDisplayOrder = this.options.ignoreFieldRoleDisplayOrder ?? false;
}

export namespace VisualType {
  export function define<
    TComponent extends VisualComponent<TOptions, TState, TStyle>,
    TOptions   extends Visual.Options = Visual.Options,
    TState     extends Visual.State = Visual.State,
    TStyle     extends VisualStyle = VisualStyle,
    TVisual    extends Visual<TOptions, TState, TStyle> = Visual<TOptions, TState, TStyle>,
    TRoles     extends VisualFieldRoleTypeMap = VisualFieldRoleTypeMap>(
      component: ComponentType<TComponent>,
      name: string,
      fieldRoles: TRoles,
      options?: VisualTypeOptions<TOptions>
  ) {
    return new class extends VisualType<TOptions, TState, TStyle, TVisual> {
      constructor() {
        super(name, component, {
          fieldRoles,
          className: name,
          canReuse: false,
          ...options
        });
      }
    }();
  }
}
